import { useEffect, useMemo, useState } from 'react';
import { CalendarDays, ChevronLeft, ChevronRight, ChevronsUpDown } from 'lucide-react';
import { useTranslation } from 'react-i18next';

import { Popover, PopoverContent, PopoverTrigger } from '@/components/ui/popover';

type PickerView = 'day' | 'month' | 'year';

type DayQualityDatePickerProps = {
  value: string;
  onSelect: (nextDate: string) => void | Promise<void>;
  disabled?: boolean;
  minDate: string;
  maxDate: string;
  hitCost?: number;
};

const MONTHS = ['Jan', 'Feb', 'Mar', 'Apr', 'May', 'Jun', 'Jul', 'Aug', 'Sep', 'Oct', 'Nov', 'Dec'];
const MONTHS_LONG = [
  'January',
  'February',
  'March',
  'April',
  'May',
  'June',
  'July',
  'August',
  'September',
  'October',
  'November',
  'December',
];
const WEEKDAY_LABELS = ['S', 'M', 'T', 'W', 'T', 'F', 'S'];

function pad(value: number) {
  return String(value).padStart(2, '0');
}

function toIsoDate(date: Date) {
  return `${date.getFullYear()}-${pad(date.getMonth() + 1)}-${pad(date.getDate())}`;
}

function fromIsoDate(isoDate: string) {
  const match = /^(\d{4})-(\d{2})-(\d{2})$/.exec(isoDate);
  if (!match) return new Date();
  return new Date(Number(match[1]), Number(match[2]) - 1, Number(match[3]));
}

function getLocalTodayIso() {
  return toIsoDate(new Date());
}

function isSameDay(left: Date, right: Date) {
  return (
    left.getFullYear() === right.getFullYear() &&
    left.getMonth() === right.getMonth() &&
    left.getDate() === right.getDate()
  );
}

function clampDate(date: Date, minDate: Date, maxDate: Date) {
  if (date < minDate) return minDate;
  if (date > maxDate) return maxDate;
  return date;
}

export default function DayQualityDatePicker({
  value,
  onSelect,
  disabled = false,
  minDate,
  maxDate,
  hitCost = 20,
}: DayQualityDatePickerProps) {
  const { t } = useTranslation('chat');
  const [open, setOpen] = useState(false);
  const [view, setView] = useState<PickerView>('day');

  const selectedDate = useMemo(() => fromIsoDate(value), [value]);
  const minDateObj = useMemo(() => fromIsoDate(minDate), [minDate]);
  const maxDateObj = useMemo(() => fromIsoDate(maxDate), [maxDate]);
  const todayDate = useMemo(() => fromIsoDate(getLocalTodayIso()), []);

  const [viewMonth, setViewMonth] = useState<Date>(() => new Date(selectedDate.getFullYear(), selectedDate.getMonth(), 1));

  useEffect(() => {
    setViewMonth(new Date(selectedDate.getFullYear(), selectedDate.getMonth(), 1));
  }, [selectedDate]);

  const formattedWeekday = useMemo(() => {
    return new Intl.DateTimeFormat(undefined, { weekday: 'long' }).format(selectedDate).toUpperCase();
  }, [selectedDate]);

  const formattedDate = useMemo(() => {
    return new Intl.DateTimeFormat(undefined, {
      month: 'long',
      day: 'numeric',
      year: 'numeric',
    }).format(selectedDate);
  }, [selectedDate]);

  const monthLabel = MONTHS_LONG[viewMonth.getMonth()];
  const yearLabel = viewMonth.getFullYear();

  const dayCells = useMemo(() => {
    const firstOfMonth = new Date(viewMonth.getFullYear(), viewMonth.getMonth(), 1);
    const start = new Date(firstOfMonth);
    start.setDate(start.getDate() - start.getDay());
    return Array.from({ length: 42 }, (_, index) => {
      const cellDate = new Date(start);
      cellDate.setDate(start.getDate() + index);
      return cellDate;
    });
  }, [viewMonth]);

  const yearWindowStart = Math.floor(viewMonth.getFullYear() / 12) * 12;
  const yearCells = useMemo(() => {
    return Array.from({ length: 12 }, (_, index) => yearWindowStart + index);
  }, [yearWindowStart]);

  const triggerToday = useMemo(() => {
    return isSameDay(selectedDate, todayDate);
  }, [selectedDate, todayDate]);

  const chooseDate = async (nextDate: Date) => {
    const clamped = clampDate(nextDate, minDateObj, maxDateObj);
    const nextIso = toIsoDate(clamped);
    setOpen(false);
    setView('day');
    if (nextIso === value) {
      return;
    }
    await onSelect(nextIso);
  };

  const shiftViewMonth = (delta: number) => {
    if (view === 'day') {
      const next = new Date(viewMonth.getFullYear(), viewMonth.getMonth() + delta, 1);
      if (next > new Date(maxDateObj.getFullYear(), maxDateObj.getMonth(), 1)) return;
      if (next < new Date(minDateObj.getFullYear(), minDateObj.getMonth(), 1)) return;
      setViewMonth(next);
      return;
    }

    if (view === 'month') {
      const next = new Date(viewMonth.getFullYear() + delta, viewMonth.getMonth(), 1);
      if (next.getFullYear() < minDateObj.getFullYear() || next.getFullYear() > maxDateObj.getFullYear()) return;
      setViewMonth(next);
      return;
    }

    const next = new Date(viewMonth.getFullYear() + delta * 12, viewMonth.getMonth(), 1);
    setViewMonth(next);
  };

  const headerTitle = (() => {
    if (view === 'day') return `${monthLabel} ${yearLabel}`;
    if (view === 'month') return String(yearLabel);
    return `${yearWindowStart} - ${yearWindowStart + 11}`;
  })();

  return (
    <Popover open={open} onOpenChange={(nextOpen) => {
      setOpen(nextOpen);
      if (!nextOpen) setView('day');
    }}>
      <PopoverTrigger asChild>
        <button
          type="button"
          disabled={disabled}
          className={`flex min-w-[15rem] items-center gap-3 rounded-xl border border-gray-200 bg-white px-4 py-3 text-left shadow-sm transition hover:border-gray-300 hover:shadow ${
            disabled ? 'cursor-not-allowed opacity-60' : ''
          }`}
        >
          <div className="flex h-10 w-10 items-center justify-center rounded-full bg-indigo-50 text-indigo-600">
            <CalendarDays className="h-4 w-4" />
          </div>
          <div className="min-w-0 flex-1">
            <div className="text-[10px] font-semibold uppercase tracking-[0.22em] text-gray-500">{formattedWeekday}</div>
            <div className="truncate text-sm font-semibold text-gray-900">{formattedDate}</div>
          </div>
          <ChevronsUpDown className={`h-4 w-4 text-gray-400 transition-transform ${open ? 'rotate-180' : ''}`} />
        </button>
      </PopoverTrigger>
      <PopoverContent
        align="end"
        sideOffset={8}
        className="w-[min(22rem,calc(100vw-1.5rem))] overflow-hidden rounded-2xl border border-gray-200 bg-white p-0 shadow-xl"
      >
        <div className="border-b border-gray-100 bg-gradient-to-br from-indigo-50 via-white to-white px-4 py-4">
          <div className="flex items-center justify-between gap-3">
            <button
              type="button"
              onClick={() => shiftViewMonth(-1)}
              className="flex h-9 w-9 items-center justify-center rounded-full border border-gray-200 text-gray-700 transition hover:border-gray-300 hover:bg-white"
              aria-label="Previous"
            >
              <ChevronLeft className="h-4 w-4" />
            </button>
            <button
              type="button"
              onClick={() => setView((current) => (current === 'day' ? 'month' : current === 'month' ? 'year' : 'year'))}
              className="min-w-0 rounded-full px-3 py-1 text-sm font-semibold text-gray-900 transition hover:bg-white"
            >
              {headerTitle}
            </button>
            <button
              type="button"
              onClick={() => shiftViewMonth(1)}
              className="flex h-9 w-9 items-center justify-center rounded-full border border-gray-200 text-gray-700 transition hover:border-gray-300 hover:bg-white"
              aria-label="Next"
            >
              <ChevronRight className="h-4 w-4" />
            </button>
          </div>
        </div>

        {view === 'day' && (
          <div className="px-4 py-4">
            <div className="mb-3 grid grid-cols-7 gap-1 text-center text-[11px] font-semibold uppercase tracking-[0.18em] text-gray-400">
              {WEEKDAY_LABELS.map((day, index) => (
                <div key={`${day}-${index}`} className="py-1">
                  {day}
                </div>
              ))}
            </div>
            <div className="grid grid-cols-7 gap-1">
              {dayCells.map((cellDate) => {
                const outsideMonth = cellDate.getMonth() !== viewMonth.getMonth();
                const selected = isSameDay(cellDate, selectedDate);
                const isToday = isSameDay(cellDate, todayDate);
                const disabledDate = cellDate < minDateObj || cellDate > maxDateObj;
                return (
                  <button
                    key={toIsoDate(cellDate)}
                    type="button"
                    disabled={disabledDate || disabled}
                    onClick={() => void chooseDate(cellDate)}
                    className={`relative flex h-10 items-center justify-center rounded-xl text-sm transition ${
                      selected
                        ? 'bg-gray-900 font-semibold text-white shadow-sm'
                        : outsideMonth
                          ? 'text-gray-300'
                          : 'text-gray-700 hover:bg-gray-50'
                    } ${isToday && !selected ? 'border border-indigo-200 text-indigo-700' : ''} ${
                      disabledDate || disabled ? 'cursor-not-allowed opacity-40 hover:bg-transparent' : ''
                    }`}
                  >
                    {cellDate.getDate()}
                  </button>
                );
              })}
            </div>
          </div>
        )}

        {view === 'month' && (
          <div className="grid grid-cols-3 gap-2 px-4 py-4">
            {MONTHS.map((month, monthIndex) => {
              const candidate = new Date(viewMonth.getFullYear(), monthIndex, 1);
              const disabledMonth =
                candidate > new Date(maxDateObj.getFullYear(), maxDateObj.getMonth(), 1) ||
                candidate < new Date(minDateObj.getFullYear(), minDateObj.getMonth(), 1);
              const active = monthIndex === viewMonth.getMonth();
              return (
                <button
                  key={month}
                  type="button"
                  disabled={disabledMonth || disabled}
                  onClick={() => {
                    setViewMonth(candidate);
                    setView('day');
                  }}
                  className={`rounded-xl border px-3 py-3 text-sm font-medium transition ${
                    active ? 'border-gray-900 bg-gray-900 text-white' : 'border-gray-200 text-gray-700 hover:bg-gray-50'
                  } ${(disabledMonth || disabled) ? 'cursor-not-allowed opacity-40 hover:bg-transparent' : ''}`}
                >
                  {month}
                </button>
              );
            })}
          </div>
        )}

        {view === 'year' && (
          <div className="grid grid-cols-3 gap-2 px-4 py-4">
            {yearCells.map((year) => {
              const disabledYear = year < minDateObj.getFullYear() || year > maxDateObj.getFullYear();
              const active = year === viewMonth.getFullYear();
              return (
                <button
                  key={year}
                  type="button"
                  disabled={disabledYear || disabled}
                  onClick={() => {
                    setViewMonth(new Date(year, viewMonth.getMonth(), 1));
                    setView('month');
                  }}
                  className={`rounded-xl border px-3 py-3 text-sm font-medium transition ${
                    active ? 'border-gray-900 bg-gray-900 text-white' : 'border-gray-200 text-gray-700 hover:bg-gray-50'
                  } ${(disabledYear || disabled) ? 'cursor-not-allowed opacity-40 hover:bg-transparent' : ''}`}
                >
                  {year}
                </button>
              );
            })}
          </div>
        )}

        <div className="border-t border-gray-100 bg-gray-50 px-4 py-3">
          <div className="mb-3 text-[11px] text-gray-500">
            {t('chat.day_quality.hit_cost_hint', 'Changing the date will use {{count}} hits', { count: hitCost })}
          </div>
          <div className="flex items-center justify-between gap-3">
            <button
              type="button"
              disabled={disabled}
              onClick={() => void chooseDate(todayDate)}
              className={`rounded-lg border border-gray-200 bg-white px-3 py-2 text-xs font-semibold text-gray-700 transition hover:bg-gray-100 ${
                disabled ? 'cursor-not-allowed opacity-60' : ''
              }`}
            >
              {t('chat.day_quality.today', 'Today')}
            </button>
            <div className="text-[11px] font-medium text-gray-500">
              {triggerToday ? t('chat.day_quality.today', 'Today') : formattedDate}
            </div>
          </div>
        </div>
      </PopoverContent>
    </Popover>
  );
}
